// Agents (engagement, DESIGN §3, DATA_CONTRACT N): live runs — role card, task, guidance version, elapsed since the engine's
// start stamp, last command — with Pause · Steer · Take over · Stop; the history timeline of every run; per-run feedback
// (good / needs work / wrong + the person's comment → feedback.add); a full trace read on demand (run.trace).
// Pause · Resume · Steer · Take over · Stop are run.pause / run.resume / run.message / run.takeover / run.end --outcome abandoned,
// each open or closed (with the engine's reason) from the run's published controls; the control history is in the timeline.
import { useState } from "react";
import { Link } from "react-router-dom";
import { AgentChip, Button, Card, DocView, EmptyState, StatusPill, Timeline } from "../../components";
import type { AgentRun } from "../../data/types";
import { ago, fmtStamp, plural } from "../../lib/format";
import { useEngagement } from "./Layout";
import { RunControlButtons, RunControlState, controlEvents } from "../agentControls";
import { Act, useAct, useReadRequest, type Open } from "./shared";

const live = (r: AgentRun) => !r.endedAt && r.state !== "ended";

export function AgentsTab() {
  const { model } = useEngagement();
  const { open, drawer } = useAct();
  const active = model.runs.filter(live);
  const done = model.runs.filter((r) => !live(r));
  const [only, setOnly] = useState<string | null>(null);
  const shown = only ? model.runs.filter((r) => r.id === only) : model.runs;
  return (
    <div className="space-y-4">
      <Card title={`Live runs · ${active.length}`} bodyClass="p-3">
        {active.length === 0 ? <EmptyState title="No agent is working on this engagement now.">Runs appear here while they work, with their last command and controls.</EmptyState> : (
          <ul className="space-y-3">{active.map((r) => <LiveRun key={r.id} r={r} open={open} />)}</ul>
        )}
      </Card>

      <Card title="History" bodyClass="p-3" action={
        <label className="flex items-center gap-1.5 text-xs text-muted">Run
          <select className="rounded border border-line bg-paper px-1 py-0.5 text-xs" value={only ?? ""} onChange={(e) => setOnly(e.target.value || null)} aria-label="Show one run">
            <option value="">all</option>{model.runs.map((r) => <option key={r.id} value={r.id}>{r.id} · {r.roleCard}</option>)}
          </select>
        </label>}>
        {model.runs.length === 0 ? <EmptyState title="No agent runs on this engagement yet." /> : (
          <Timeline label="Agent run history" events={shown.flatMap((r) => [
            { id: `s${r.id}`, at: r.startedAt, tone: "system" as const, title: <span className="inline-flex flex-wrap items-center gap-1">Run {r.id} started <AgentChip name={r.roleCard} /> for {r.person ?? "—"}</span>, detail: r.task ?? undefined },
            ...r.commands.map((c) => ({ id: `${r.id}c${c.seq}`, at: c.at, tone: "agent" as const, title: <span className="font-mono text-xs">run {r.id} · #{c.seq} {c.command}{c.ok ? "" : " · refused by the gate"}</span> })),
            ...r.refusals.map((f, i) => ({ id: `${r.id}f${i}`, at: f.at, tone: "refusal" as const, title: <span>Run {r.id} refused: <span className="font-mono text-xs">{f.command}</span></span>, detail: f.message })),
            ...controlEvents(r, "c"),
            ...(r.endedAt ? [{ id: `e${r.id}`, at: r.endedAt, tone: "system" as const, title: <span>Run {r.id} ended{r.outcome ? ` — agent's account: ${r.outcome}` : ""}</span>, detail: r.summary ?? undefined }] : []),
          ]).sort((a, b) => b.at.localeCompare(a.at))} />
        )}
      </Card>

      {done.length > 0 && (
        <Card title={`Ended runs · ${done.length}`} bodyClass="p-3">
          <ul className="divide-y divide-line">{done.map((r) => <EndedRun key={r.id} r={r} open={open} />)}</ul>
        </Card>
      )}
      {drawer}
    </div>
  );
}

function RunHead({ r }: { r: AgentRun }) {
  const { id } = useEngagement();
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <AgentChip name={r.roleCard} /><span className="font-medium">{r.task ?? "—"}</span>
        <StatusPill state={r.state === "active" ? "running" : r.state} live={r.state === "active"} />
      </div>
      <p className="text-xs text-muted">Run {r.id} · {r.agent} {r.toolVersion ?? ""} · works for {r.person ?? "—"} (owner of record) · guidance {r.guidanceVersion ?? "—"} · started {fmtStamp(r.startedAt)} ({ago(r.startedAt)}){r.lastCommand ? ` · last command ${r.lastCommand}` : ""}
        {r.workpaper && <> · <Link className="text-brand hover:underline" to={`/e/${id}/details/wp/${r.workpaper}`}>{r.workpaper}</Link></>}</p>
      <RunControlState r={r} />
      {r.summary && <p className="rounded border border-agent-line bg-agent-bg px-2 py-1 text-sm text-agent">Agent's narration (not status): {r.summary}</p>}
      {r.refusals.length > 0 && <ul className="text-xs text-blocked">{r.refusals.map((f, i) => <li key={i}>Refused {fmtStamp(f.at)}: <span className="font-mono">{f.command}</span> — {f.message}</li>)}</ul>}
    </>
  );
}

function Feedback({ r, open }: { r: AgentRun; open: Open }) {
  const target = r.workpaper ? { target_kind: "workpaper", target: r.workpaper } : { target_kind: "audit", target: String(r.commands[r.commands.length - 1]?.seq ?? r.id) };
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs">
      <span className="text-muted">Feedback:</span>
      {(["good", "needs_work", "wrong"] as const).map((k) => (
        <Act key={k} command="feedback.add" title={`Feedback on run ${r.id}: ${k.replace("_", " ")}`} open={open} fixed={{ ...target, rating: k }} label={k.replace("_", " ")} />
      ))}
    </span>
  );
}

function Trace({ r }: { r: AgentRun }) {
  const read = useReadRequest();
  return (
    <div className="space-y-1">
      <Button size="sm" onClick={() => void read.ask("run.trace", { run: Number(r.id) }, `Full trace of run ${r.id}`)}>Full trace…</Button>
      {read.id && !read.result && !read.error && <p className="text-xs text-muted">Asked the engine — the trace shows here when it has run the read.</p>}
      {read.error && <p className="text-xs text-blocked">{read.error}</p>}
      {read.result != null && <div className="max-h-80 overflow-auto rounded border border-line p-2"><DocView doc={read.result} /></div>}
    </div>
  );
}

function LiveRun({ r, open }: { r: AgentRun; open: Open }) {
  return (
    <li className="space-y-2 rounded-lg border border-agent-line p-3">
      <RunHead r={r} />
      <RunControlButtons r={r} open={open} />
      <div className="flex flex-wrap items-start justify-between gap-2"><Feedback r={r} open={open} /><Trace r={r} /></div>
    </li>
  );
}

function EndedRun({ r, open }: { r: AgentRun; open: Open }) {
  return (
    <li className="space-y-1.5 py-2 text-sm">
      <RunHead r={r} />
      <p className="text-xs text-muted">Ended {fmtStamp(r.endedAt)}{r.outcome ? ` · agent's account: ${r.outcome}` : ""} · {plural(r.commandCount, "command")} published</p>
      <div className="flex flex-wrap items-start justify-between gap-2"><Feedback r={r} open={open} /><Trace r={r} /></div>
    </li>
  );
}
